import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Globe, ChevronLeft, Users, ExternalLink, Calendar, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from 'date-fns';
import { useTranslation } from '@/components/utils/translations';
import {
  translateBvoLabel,
  translateIncumbentSentiment,
  translateProcurementFriendliness,
} from '@/lib/accountClassificationLabels';
import { notify } from '@/components/ui/notify';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant, normalizeTenantId, isLibraryAccountPageTenant } from '@/lib/tenantUtils';
import {
  EVASYS_TARGET_PRODUCT_OPTIONS,
  LEARNWISE_TARGET_PRODUCT_OPTIONS,
  CAYUSE_TARGET_PRODUCT_OPTIONS,
  deriveEvasysTargetProductValue,
  deriveLearnwiseTargetProductValue,
  deriveCayuseTargetProductValue,
  isSysExamProductContextTenant,
} from '@/lib/accountTargetProducts';
import ListedForSaleBadge, { isListedForSale } from '@/components/accounts/ListedForSaleBadge';
import AccountRenewalRiskBadge from '@/components/accounts/AccountRenewalRiskBadge';
import AccountOverflowMenu from '@/components/accounts/AccountOverflowMenu';
import { AI_READINESS_DEFINITIONS, aiReadinessBadgeLabel, isUntrustedAccountLogoUrl, accountNameInitials } from '@/lib/marketExperience';
import { canShowRemoveAccountAction, isMarketModuleTenantId } from '@/lib/marketExperienceApi';
import { usePilotStatus } from '@/hooks/usePilotStatus';
import { resolveAccountFit } from '@/lib/accountFitDisplay';
import { hasDisplayableTier } from '@/lib/accountTier';
import { formatEnrolmentBadge } from '@/lib/enrolmentDisplay';

const getTierStyling = (tier) => {
    const match = tier?.toString().match(/(?:^T|^Tier\s*)([1-4])/i);
    const tierNum = match ? match[1] : null;
    switch (tierNum) {
        case '1': return 'bg-green-100 text-green-800 border-green-200';
        case '2': return 'bg-blue-100 text-blue-800 border-blue-200';
        case '3': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case '4': return 'bg-gray-100 text-gray-800 border-gray-200';
        default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
};

const EDITABLE_TIER_PATTERN = /^(T[1-4]|Tier\s*[1-4])$/i;

const isEditableTier = (tier) => {
  if (!tier || tier === 'N/A') return false;
  return EDITABLE_TIER_PATTERN.test(String(tier).trim());
};

const normalizeTier = (tier) => {
  if (!tier) return 'N/A';
  const raw = tier.toString().trim();
  if (/^Tier\s*\d+\s*[-–—]/.test(raw)) return raw;
  const match = raw.match(/Tier\s*(\d)/i);
  if (match) return `Tier ${match[1]}`;
  const tMatch = raw.match(/^T(\d)$/i);
  if (tMatch) return `Tier ${tMatch[1]}`;
  if (/^\d$/.test(raw)) return `Tier ${raw}`;
  return raw;
};

// NEW: Get department badge color based on score
const getDeptScoreColor = (score) => {
  const numScore = Number(score) || 0;
  if (numScore >= 90) return 'bg-green-100 text-green-700 border-green-200';
  if (numScore >= 75) return 'bg-blue-100 text-blue-700 border-blue-200';
  if (numScore >= 60) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

export default function AccountHeader({
  account,
  startChatSession,
  currentTenant,
  topDepartments = [],
  onTierUpdate,
  onTargetProductsUpdate,
  marketBadges = null,
  marketModuleOn = null,
  onRemoveFromList,
  renewalRisk = null,
}) {
  const { t, language } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const scoreInfo = resolveAccountFit(account);
  const fitLabel = scoreInfo.label;
  const fitScored = scoreInfo.scored;
  const fitUnscoredMessage = account.account_fit_label?.trim() || 'Not scored';
  const rawAccountTier = account?.tier || account?.account_tier || '';
  const effectiveTenant = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant || contextTenant,
    contextUser,
    account?.tenant_id || 'instructure'
  );
  const { status: pilotStatus, pilotMode } = usePilotStatus(effectiveTenant);
  const suppressRemoveForPilot = Boolean(
    pilotMode || (pilotStatus == null && isMarketModuleTenantId(effectiveTenant)),
  );
  const showRemoveFromList =
    typeof onRemoveFromList === 'function' &&
    canShowRemoveAccountAction(effectiveTenant, marketModuleOn, suppressRemoveForPilot);
  const showUnscoredFit = !fitScored;
  const isCalmedica = effectiveTenant === 'calmedica';
  const isCHUBordeaux = isCalmedica && account.account_name === 'CHU de Bordeaux';
  const isSysExamTenant = isSysExamProductContextTenant(effectiveTenant);
  const isLearnwise = effectiveTenant === 'learnwise';
  const isCayuse = effectiveTenant === 'cayuse';
  const showTenantTargetProducts =
    (isSysExamTenant || isLearnwise || isCayuse) && onTargetProductsUpdate;
  const tenantTargetProductValue = isSysExamTenant
    ? deriveEvasysTargetProductValue(account?.target_products)
    : isLearnwise
      ? deriveLearnwiseTargetProductValue(account?.target_products)
      : deriveCayuseTargetProductValue(account?.target_products);
  const tenantTargetProductOptions = isSysExamTenant
    ? EVASYS_TARGET_PRODUCT_OPTIONS
    : isLearnwise
      ? LEARNWISE_TARGET_PRODUCT_OPTIONS
      : CAYUSE_TARGET_PRODUCT_OPTIONS;
  // Tenants that should show buying_model (+ rationale tooltip)
  const BUYING_MODEL_TENANTS = new Set([
    'prowise',
    'edqwest',
    'faria',
    'sebit',
    'skillwell',
    'kairadigital',
    'wayground',
  ]);
  const tenantShowsBuyingModel = (id) => {
    const normalized = String(normalizeTenantId(id) || '').toLowerCase();
    return BUYING_MODEL_TENANTS.has(normalized) || isLibraryAccountPageTenant(id);
  };
  const shouldShowBuyingModel = Boolean(
    account.buying_model &&
      (tenantShowsBuyingModel(effectiveTenant) ||
        tenantShowsBuyingModel(currentTenant) ||
        tenantShowsBuyingModel(account?.tenant_id)),
  );  
  const [accountTier, setAccountTier] = useState(normalizeTier(rawAccountTier));
  const tierEditable = isEditableTier(rawAccountTier) || isEditableTier(accountTier);
  const [isUpdatingTier, setIsUpdatingTier] = useState(false);
  const [isUpdatingTargetProducts, setIsUpdatingTargetProducts] = useState(false);

  const handleTierChange = async (newTier) => {
    setIsUpdatingTier(true);
    try {
      const tenantId = effectiveTenant || 'instructure';
      
      const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? 'https://api.neogtm.com' 
        : 'https://api.neogtm.com';

      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/accounts/${account.account_id || account.id}/tier`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            tenant_id: tenantId,
            tier: newTier
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const displayTier = normalizeTier(data.account.account_tier);
        setAccountTier(displayTier);
        
        // Update parent component's account state
        if (onTierUpdate) {
          onTierUpdate(data.account.account_tier);
        }
        
        // Clear market page cache so Tiering Snapshot refreshes
        try {
          sessionStorage.removeItem('market-cache-v2');
        } catch {}
        
        notify({
          title: 'Tier Updated',
          description: `Account tier changed to ${newTier}`,
          variant: 'success'
        });
      } else {
        const error = await response.json();
        notify({
          title: 'Error',
          description: error.error || 'Failed to update tier',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating tier:', error);
      notify({
        title: 'Error',
        description: 'Failed to update tier',
        variant: 'destructive'
      });
    } finally {
      setIsUpdatingTier(false);
    }
  };

  const handleTargetProductsChange = async (newValue) => {
    if (!onTargetProductsUpdate) return;
    setIsUpdatingTargetProducts(true);
    try {
      const valueToSave = newValue === 'none' || !newValue ? null : newValue;
      await onTargetProductsUpdate(valueToSave);
    } catch (err) {
      console.error('Error updating target products:', err);
    } finally {
      setIsUpdatingTargetProducts(false);
    }
  };

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-4 md:p-6 relative">
        <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-3 md:gap-4 flex-grow min-w-0">
                {!isUntrustedAccountLogoUrl(account.logo_url) ? (
                    <img src={account.logo_url} alt={`${account.account_name} logo`} className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-md border p-1 bg-white flex-shrink-0"/>
                ) : (
                    <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0 flex items-center justify-center bg-gray-100 rounded-md border border-gray-200">
                        <span className="text-sm md:text-base font-semibold text-[#5C6B7A] tracking-wide">
                          {accountNameInitials(account.account_name)}
                        </span>
                    </div>
                )}
                <div className="flex-grow min-w-0">
                    <div className="flex flex-col gap-1 md:gap-2">
                        <div className="flex items-start gap-2 md:pr-24">
                            <h1 className="text-lg md:text-3xl font-bold text-[#0A1A2F] break-words min-w-0 flex-1">{account.account_name}</h1>
                            {showRemoveFromList && (
                                <AccountOverflowMenu
                                    className="flex-shrink-0 mt-0.5 md:mt-1"
                                    onRemove={onRemoveFromList}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {hasDisplayableTier(account.account_tier || accountTier) && (
                                tierEditable ? (
                                <Select 
                                    value={accountTier} 
                                    onValueChange={handleTierChange}
                                    disabled={isUpdatingTier}
                                >
                                    <SelectTrigger className="h-7 w-auto min-w-[80px] border-0 p-0 hover:opacity-80">
                                        <SelectValue>
                                            {isUpdatingTier ? (
                                                <Badge className="px-2 py-1 text-xs font-semibold flex items-center gap-1">
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                    ...
                                                </Badge>
                                            ) : (
                                                <Badge className={`px-2 py-1 text-xs font-semibold border ${getTierStyling(accountTier)}`}>
                                                    {accountTier || 'N/A'}
                                                </Badge>
                                            )}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="T1">
                                            <Badge className="bg-green-100 text-green-800 border-green-200">Tier 1</Badge>
                                        </SelectItem>
                                        <SelectItem value="T2">
                                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">Tier 2</Badge>
                                        </SelectItem>
                                        <SelectItem value="T3">
                                            <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Tier 3</Badge>
                                        </SelectItem>
                                        <SelectItem value="T4">
                                            <Badge className="bg-gray-100 text-gray-800 border-gray-200">Tier 4</Badge>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                ) : (
                                <Badge className={`px-2 py-1 text-xs font-semibold border ${getTierStyling(account.account_tier || accountTier)}`}>
                                    {account.account_tier || accountTier}
                                </Badge>
                                )
                            )}
                            {renewalRisk ? <AccountRenewalRiskBadge row={renewalRisk} /> : null}
                            {showTenantTargetProducts && (
                                <Select
                                    value={tenantTargetProductValue ? String(tenantTargetProductValue) : 'none'}
                                    onValueChange={(v) => handleTargetProductsChange(v === 'none' ? '' : v)}
                                    disabled={isUpdatingTargetProducts}
                                >
                                    <SelectTrigger className="h-7 w-auto min-w-[90px] border-0 p-0 hover:opacity-80">
                                        <SelectValue>
                                            {isUpdatingTargetProducts ? (
                                                <Badge className="px-2 py-1 text-xs font-semibold flex items-center gap-1">
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                    ...
                                                </Badge>
                                            ) : (
                                                <Badge variant="secondary" className="bg-[#00C2C2]/10 text-[#00A3A3] border border-[#00C2C2]/30 text-xs px-2 py-1">
                                                    {tenantTargetProductValue || 'Target product'}
                                                </Badge>
                                            )}
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">
                                            <span className="text-gray-500">Select product</span>
                                        </SelectItem>
                                        {tenantTargetProductOptions.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            {isCHUBordeaux && (
                                <Badge className="bg-blue-500 text-white text-xs px-2 py-1">
                                    {t('client')}
                                </Badge>
                            )}
                            {isListedForSale(account) && <ListedForSaleBadge account={account} />}
                            {fitScored && (
                                <Badge className={`${scoreInfo.color} border text-xs px-2 py-1 flex-shrink-0 md:hidden`}>
                                    {fitLabel} ({scoreInfo.score})
                                </Badge>
                            )}
                            {showUnscoredFit && (
                                <Badge variant="secondary" className="bg-gray-100 text-[#5C6B7A] text-xs px-2 py-1 flex-shrink-0 md:hidden">
                                    Not scored
                                </Badge>
                            )}
                        </div>
                    </div>
                    {account.website_url && (
                        <a href={account.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs md:text-sm text-[#5C6B7A] hover:text-[#0A1A2F] hover:underline gap-1 mt-1 break-all">
                            <Globe className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                            <span className="break-all">{account.website_url.replace(/^https?:\/\//, '')}</span>
                        </a>
                    )}
                </div>
            </div>

            <div className="hidden md:flex flex-col items-center gap-1 flex-shrink-0 md:absolute md:right-4 md:top-4">
                {showUnscoredFit ? (
                  <div className="max-w-[140px] text-center">
                    <div className="w-16 h-16 mx-auto bg-gray-100 text-[#5C6B7A] rounded-full flex items-center justify-center text-[10px] font-semibold px-1">
                      —
                    </div>
                    <span className="text-[10px] font-medium text-[#5C6B7A] leading-tight block mt-1">
                      {fitUnscoredMessage}
                    </span>
                  </div>
                ) : (
                  <>
                    <TooltipProvider delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className={`w-16 h-16 ${scoreInfo.color} rounded-full flex items-center justify-center font-bold text-xl cursor-help transition-transform hover:scale-105`}>
                                    {scoreInfo.score}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" align="center" className="w-[90vw] max-w-[600px] md:w-[600px] bg-[#0A1A2F] text-white border-0 shadow-lg z-[9999]">
                                <p className="text-sm font-semibold mb-1 text-[#00C2C2]">{t('accountFitRationale')}</p>
                                <p className="text-sm text-white leading-relaxed">{account.score_rationale || t('noRationaleAvailable')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <span className={`text-xs font-semibold ${scoreInfo.textColor}`}>{fitLabel}</span>
                  </>
                )}
            </div>
        </div>

        <div className="flex flex-wrap gap-1.5 md:gap-2 items-center">
          {account.country && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {account.country}
            </Badge>
          )}
          {account.sector && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {account.sector}
            </Badge>
          )}
          {account.offers_t_levels === true && (
            <Badge
              variant="secondary"
              className="bg-[#00C2C2]/10 text-[#00A3A3] border border-[#00C2C2]/30"
              title={account.offers_t_levels_source || 'T Level provider'}
            >
              T Levels
            </Badge>
          )}
          {/* UPDATED: Show top departments inline with color coding */}
          {isCalmedica && topDepartments && topDepartments.length > 0 && topDepartments.map((dept, idx) => (
            <TooltipProvider key={idx} delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="cursor-help">
                    <Badge variant="secondary" className={`border text-xs ${getDeptScoreColor(dept.department_fit_score)}`}>
                      {dept.department} ({Number(dept.department_fit_score) || 0})
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  align="center"
                  className="max-w-[90vw] md:max-w-md bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                >
                  <p className="text-sm font-semibold text-[#00C2C2] mb-2">{dept.department} {t('departmentFitRationale')}</p>
                  <p className="text-xs leading-relaxed">
                    {dept.department_fit_rationale || t('noRationaleAvailable')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          {account.primary_location && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {account.primary_location}
            </Badge>
          )}
          {account.subscribers != null && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {Number(account.subscribers).toLocaleString('en-US')} Subscribers
            </Badge>
          )}
          {account.enrolment != null && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700">
              {formatEnrolmentBadge(account.enrolment, account.enrolment_measure, t('students'))}
            </Badge>
          )}
          {marketBadges?.financial_capacity?.band != null && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-[#ECFFFE] text-[#0A1A2F] border border-[#00C2C2]/30">
                      {marketBadges.financial_capacity.band}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-[#0A1A2F] text-white border-0 shadow-lg px-2 py-1 max-w-xs">
                  <p className="text-xs font-semibold text-[#00C2C2] mb-0.5">Financial capacity</p>
                  <p className="text-xs">{marketBadges.financial_capacity.means || marketBadges.financial_capacity.band}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {(marketBadges?.ai_readiness?.label || marketBadges?.ai_readiness?.level != null) && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-[#ECFFFE] text-[#0A1A2F] border border-[#00C2C2]/30">
                      {aiReadinessBadgeLabel(marketBadges.ai_readiness) || 'AI readiness'}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="w-[90vw] max-w-[420px] max-h-[70vh] overflow-y-auto bg-[#0A1A2F] text-white border-0 shadow-lg p-3 z-[9999]"
                >
                  <p className="text-xs font-semibold text-[#00C2C2] mb-1.5">AI readiness</p>
                  {marketBadges.ai_readiness.basis ? (
                    <p className="text-xs mb-2 leading-relaxed whitespace-normal break-words">
                      {marketBadges.ai_readiness.basis}
                    </p>
                  ) : marketBadges.ai_readiness.means ? (
                    <p className="text-xs mb-2 leading-relaxed">{marketBadges.ai_readiness.means}</p>
                  ) : null}
                  {marketBadges.ai_readiness.basis_full || marketBadges.ai_readiness.full ? (
                    <details className="mb-2">
                      <summary className="text-[11px] text-[#00C2C2] cursor-pointer">Full assessment</summary>
                      <p className="text-[11px] mt-1 leading-relaxed whitespace-normal break-words text-white/90">
                        {marketBadges.ai_readiness.basis_full || marketBadges.ai_readiness.full}
                      </p>
                    </details>
                  ) : null}
                  <p className="text-[11px] text-white/80 mb-2 leading-relaxed">{AI_READINESS_DEFINITIONS.scale}</p>
                  <ul className="space-y-1.5">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <li
                        key={level}
                        className={`text-[11px] leading-snug ${
                          Number(marketBadges.ai_readiness.level) === level ? 'text-[#00C2C2]' : 'text-white/90'
                        }`}
                      >
                        <span className="font-semibold">{level}. </span>
                        {AI_READINESS_DEFINITIONS[level]}
                      </li>
                    ))}
                  </ul>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {account.employees && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                      {account.employees.toLocaleString()} {t('employees')}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-[#0A1A2F] text-white border-0 shadow-lg px-2 py-1">
                  <p className="text-xs">{t('numberOfEmployees')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {shouldShowBuyingModel && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {account.buying_model}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  align="center"
                  className="w-[90vw] max-w-[500px] md:w-[500px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                >
                  <p className="text-sm font-semibold mb-2 text-indigo-300">{t('buyingModelRationale')}</p>
                  <p className="text-sm leading-relaxed">
                    {account.buying_model_rationale || t('noRationaleAvailable')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {account.bvo_label && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-[#00C2C2]/10 text-[#00C2C2] border border-[#00C2C2]/20">
                      {translateBvoLabel(language, account.bvo_label)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  align="center"
                  className="w-[90vw] max-w-[500px] md:w-[500px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                >
                  <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('bvoClassification')}</p>
                  <p className="text-sm leading-relaxed">
                    {account.bvo_rationale || t('noRationaleAvailable')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {account.incumbent_sentiment && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className="bg-purple-50 text-purple-700 border border-purple-200">
                      {translateIncumbentSentiment(language, account.incumbent_sentiment)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  align="center"
                  className="w-[90vw] max-w-[500px] md:w-[500px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                >
                  <p className="text-sm font-semibold mb-2 text-purple-300">{t('incumbentSentiment')}</p>
                  <p className="text-sm leading-relaxed">
                    {account.incumbent_sentiment_rationale || t('noRationaleAvailable')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {account.procurement_friendliness && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help">
                    <Badge variant="secondary" className={`border ${
                      account.procurement_friendliness === 'Friendly' ? 'bg-green-50 text-green-700 border-green-200' :
                      account.procurement_friendliness === 'Standard' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      account.procurement_friendliness === 'Bureaucratic' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                      account.procurement_friendliness === 'Monitor' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-gray-50 text-gray-700 border-gray-200'
                    }`}>
                      {translateProcurementFriendliness(language, account.procurement_friendliness)}
                    </Badge>
                  </span>
                </TooltipTrigger>
                <TooltipContent 
                  side="top"
                  align="center"
                  className="w-[90vw] max-w-[500px] md:w-[500px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4 z-[9999]"
                >
                  <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('procurementFriendliness')}</p>
                  <p className="text-sm leading-relaxed">
                    {account.procurement_friendliness === 'Friendly' ? t('friendlyDesc') :
                     account.procurement_friendliness === 'Standard' ? t('standardDesc') :
                     account.procurement_friendliness === 'Bureaucratic' ? t('bureaucraticDesc') :
                     account.procurement_friendliness === 'Monitor' ? t('monitorDesc') :
                     t('procurementProcessClassification')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </CardContent>
    </Card>
  );
}